import toast from 'react-hot-toast';
import { useState } from "react";

import {
    handleItemDataLayerPush,
    handleOrderItemClickDatalayerPush,
} from 'Utils/dataLayerPushService';
import { dpr } from 'Utils/dpr';

import { IAlphaMiniCartModel, IOrderItem } from 'Types/Litium';
import CtaButton from 'Components/CtaButton';
import VariantSelect from './VariantSelect';
import CartItemStock from './CartItemStock';
import QuantityInputField from 'Components/Forms/QuantityInputField';
import BinIcon from 'Icons/bin.svg?react';
import {type RowSpecificVariantSelect} from 'Components/CustomerServiceCart';

const CartItemRow = ({
    orderItem,
    variantSelectData,
    updateCartRow,
    replaceCartRow,
    translations,
    returnFocus,
    showLoweredQuantityWarning,
}: {
    orderItem: IOrderItem;
    variantSelectData: RowSpecificVariantSelect;
    updateCartRow: (
        id: string,
        quantity: string,
        type: 'decrement' | 'increment' | 'delete'
    ) => void;
    replaceCartRow: (id: string, quantity: number, orderRowId: string, type: string, loweredQuantity: boolean) => void;
    translations: IAlphaMiniCartModel['translations'];
    returnFocus: string;
    showLoweredQuantityWarning: boolean;
}) => {
    const [showIncreaseWarning, setShowIncreaseWarning] = useState(false);

    const updateVariant = (articleNumber: string, quantity: number, type: string) => {
        const currentQuantity = Number(orderItem.itemQuantity.quantity);
        let newQuantity = currentQuantity;
        let loweredQuantity = false;

        if (isNaN(currentQuantity) || currentQuantity > quantity) {
            newQuantity = quantity;
            loweredQuantity = true;
        }

        replaceCartRow(articleNumber, newQuantity, orderItem.systemId, type, loweredQuantity)
    };

    const handleDecrementQuantity = (item: IOrderItem) => {
        if (Number(item.itemQuantity.quantity) === 1) {
            handleRemoveItem(item);
        } else {
            updateCartRow(item.id, item.itemQuantity.quantity, 'decrement');
        }
    };

    const handlePlusAction = () => {
        if(!orderItem.itemQuantity.canIncreaseProductAmount) {
            setShowIncreaseWarning(true);
            return;
        }

        updateCartRow(
            orderItem.id,
            orderItem.itemQuantity.quantity,
            'increment'
        );
        handleItemDataLayerPush(orderItem,'add');
    };

    const renderTotalPrices = (item: IOrderItem) => (
        <div className="flex items-center justify-between mt-auto text-xs font-bold">
            {item && item.totalPriceDiscounts?.length ? (
                <>
                    <span className="line-through">
                        {item.totalPrice}
                    </span>
                    {item.totalPriceDiscounts.map((value, i) => (
                        <span key={`${i} - ${item.id}`} className="text-persianPink">
                            {value.discountPrice}
                        </span>
                    ))}
                </>
            ) : (
                <span>{item.totalPrice}</span>
            )}
        </div>
    );

    const renderUnitPrices = (item: IOrderItem) => (
        <div className="flex items-center justify-between mt-auto text-xs">
            {item && item.unitPriceDiscounts?.length ? (
                <>
                    <span className="line-through">
                        {item.unitPrice}
                    </span>
                    {item.unitPriceDiscounts.map((price, i) => (
                        <span key={`${i} - ${item.id}`} className="text-persianPink">
                            {price}/{translations.customerServiceCartUnit}
                        </span>
                    ))}
                </>
            ) : (
                <span>{item.unitPrice}/{translations.customerServiceCartUnit}</span>
            )}
        </div>
    );

    const handleRemoveItem = (item: IOrderItem) => {
        toast(
            t => (
                <div className="flex flex-col gap-1 font-montserrat">
                    <div className="mr-7 flex gap-1">
                        <p>{translations.removeOrderRowConfirm}</p>
                    </div>
                    <div className="flex">
                        <h4 className="text-lg font-bold">{`${item.name}`}</h4>
                    </div>
                    <div className="flex gap-1">
                        <p>{translations.checkOutItemNumberLabel}:</p>
                        <p className="font-bold">{item.baseId}</p>
                    </div>
                    <div className="flex gap-1">
                        <p>{translations.checkOutColorLabel}:</p>
                        <p className="font-bold">{`${item.color}`}</p>
                    </div>
                    <div className="flex gap-1">
                        <p>{translations.checkOutSizeLabel}:</p>
                        <p className="font-bold">{`${item.size}`}</p>
                    </div>
                    <div className="mt-5 flex gap-2">
                        <CtaButton
                            autoFocus
                            type="button"
                            className="flex basis-1/2 items-center justify-center"
                            ctaType="primary"
                            onClick={() => {
                                updateCartRow(
                                    item.id,
                                    item.itemQuantity.quantity,
                                    'delete'
                                );
                                toast.dismiss(t.id);
                            }}
                        >
                            {translations.confirmRemoveItem}
                        </CtaButton>
                        <CtaButton
                            type="button"
                            className="flex basis-1/2 items-center justify-center"
                            onClick={() => toast.dismiss(t.id)}
                            ctaType="secondary"
                        >
                            {translations.abortRemoveItem}
                        </CtaButton>
                    </div>
                </div>
            ),
            {
                duration: Infinity,
                style: {
                    background: '#ffffff',
                },
            }
        );
    };

    return (
        <li className="flex bg-white">
            <div className="grow py-2 px-4 border-r border-r-[#F8F5F0]">
                <div className="flex">
                    <a
                        className="self-start"
                        href={orderItem.url}
                        onClick={() =>
                            handleOrderItemClickDatalayerPush(orderItem)
                        }
                        aria-label="#Go to product"
                    >
                        <figure>
                            <picture>
                                <source
                                    media="(max-width: 1920px)"
                                    srcSet={dpr(
                                        `${orderItem.imageBaseUrl}?width=184&auto=webp`
                                    )}
                                />
                                <img
                                    className="h-[60px] w-[45px] object-cover object-bottom"
                                    src={`${orderItem.imageBaseUrl}`}
                                    srcSet={dpr(`${orderItem.imageBaseUrl}`)}
                                    alt={orderItem.name}
                                />
                            </picture>
                        </figure>
                    </a>
                    <div className="pl-2 lg:grow flex flex-col">
                        <div className="text-xs">
                            {orderItem.id}
                        </div>
                        <a href={orderItem.url} className="self-start" aria-label="#Go to product">
                            <h4 className="font-roboto text-xs font-semibold">
                                {orderItem.name}
                            </h4>
                        </a>
                        <CartItemStock
                            translations={translations}
                            orderItem={orderItem}
                            variantSelectData={variantSelectData}
                        />
                    </div>
                </div>
                <div className="flex items-start justify-between mt-3.5 text-xs">
                    <div className="mt-2">
                    {translations.checkOutQuantity}
                    </div>
                    <div className="group grid justify-items-end cs-cart">
                        <QuantityInputField
                            value={Number(
                                orderItem.itemQuantity.quantity
                            )}
                            minusAction={() => {
                                setShowIncreaseWarning(false);
                                handleDecrementQuantity(orderItem);
                                handleItemDataLayerPush(orderItem,'remove');
                            }}
                            plusAction={handlePlusAction}
                            plusDisabled={!orderItem.itemQuantity.canIncreaseProductAmount}
                            minusDisabled={!orderItem.itemQuantity.canDecreaseAmount}
                            translations={{
                                decrementButton:translations.checkoutDecrementButton,
                                incrementButton:translations.checkoutIncreaseButton,
                                quantityLabel:translations.checkOutQuantity,
                            }}
                            showLoweredQuantityWarning={showLoweredQuantityWarning}
                            overridePlusDisabled={true}
                        />
                        {showIncreaseWarning &&
                            <div className='mt-1 text-red-500'>
                                {translations.noMoreInStock}
                            </div>
                        }
                    </div>
                </div>
            </div>
            <div className="w-44 py-2 px-4 border-r border-r-[#F8F5F0]">
                {variantSelectData && (<>
                    <VariantSelect
                        label={translations.checkOutColorLabel}
                        selected={variantSelectData.selectedColor}
                        options={variantSelectData.colors}
                        onClickHandler={(a, q) => updateVariant(a, q, 'color')}
                        baseId={orderItem.baseId}
                        returnFocus={returnFocus === 'color'}
                    />
                    <VariantSelect
                        label={translations.checkOutSizeLabel}
                        selected={variantSelectData.selectedSize}
                        options={variantSelectData.sizes}
                        onClickHandler={(a, q) => updateVariant(a, q, 'size')}
                        baseId={orderItem.baseId}
                        returnFocus={returnFocus === 'size'}
                    />
                </>)}
            </div>
            <div className="w-28 py-2 px-4 flex border-r border-r-[#F8F5F0]">
                {renderUnitPrices(orderItem)}
            </div>
            <div className="w-20 py-2 px-4 grid">
                <button
                    className="ml-auto h-4"
                    disabled={!orderItem.itemQuantity.canDecreaseAmount}
                    onClick={() => {
                        handleRemoveItem(orderItem);
                        handleItemDataLayerPush(orderItem, 'remove');
                    }}
                    type="button"
                >
                    <BinIcon className="h-4 w-4 stroke-2" />
                </button>
                {renderTotalPrices(orderItem)}
            </div>
        </li>
    );
};

export default CartItemRow;
